import { getParisNowParts, prevMonth } from '../../utils/dateUtils';
import { GET_PERIOD_MIN_PASS_HOUR, INVOICE_PERIOD, INVOICE_SECOND_PERIOD_START } from '../../constants/Invoice';

export type DateAndMonth = {
    monthNumber: number;
    year: number;
    firstHalf: boolean;
    toShow?: string;
};

const monthNameFr = (year: number, monthNumber: number): string => {
    const d = new Date(year, monthNumber - 1, 1);
    const label = new Intl.DateTimeFormat('fr-FR', { month: 'long' }).format(d);
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const toShowLabel = (p: DateAndMonth): string => {
    const m = monthNameFr(p.year, p.monthNumber);
    return `${m}, ${(p.firstHalf ? '1ère quinzaine' : '2ème quinzaine')}`;
};

export const computeLatestAvailableInvoicePeriod = (now = new Date()): DateAndMonth => {
    const { year, month, day, hour } = getParisNowParts(now);
    const prev = () => prevMonth(year, month);
    const determinePeriod = () => {
        if (day > INVOICE_SECOND_PERIOD_START ||
            (day === INVOICE_SECOND_PERIOD_START && hour >= GET_PERIOD_MIN_PASS_HOUR)) {
            return { year, monthNumber: month, firstHalf: true };
        }
        if (day === 1) {
            const p = prev();
            if (hour < GET_PERIOD_MIN_PASS_HOUR) {
                return { year: p.year, monthNumber: p.month, firstHalf: true };
            }
            return { year: p.year, monthNumber: p.month, firstHalf: false };
        }
        const p = prev();
        return { year: p.year, monthNumber: p.month, firstHalf: false };
    };
    const period = determinePeriod();
    return { ...period, toShow: toShowLabel(period) };
};

const dateToPeriod = (d: Date): DateAndMonth => {
  const year = d.getUTCFullYear();
  const monthNumber = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  const firstHalf = day <= INVOICE_PERIOD;
  return { year, monthNumber, firstHalf };
};

const comparePeriodsDesc = (a: DateAndMonth, b: DateAndMonth): number => {
  if (a.year !== b.year) {
      return b.year - a.year;
  }
  if (a.monthNumber !== b.monthNumber) {
      return b.monthNumber - a.monthNumber;
  }
  if (a.firstHalf !== b.firstHalf) {
      return (a.firstHalf ? 1 : 0) - (b.firstHalf ? 1 : 0);
  }
  return 0;
};

const toLinearIndex = (p: DateAndMonth): number => {
    const monthIndex = p.year * 12 + (p.monthNumber - 1);
    const halfOffset = p.firstHalf ? 0 : 1;
    return monthIndex * 2 + halfOffset;
};

const fromLinearIndex = (idx: number): DateAndMonth => {
    const monthIndex = Math.floor(idx / 2);
    const halfOffset = idx % 2; // 0 => H1, 1 => H2
    const year = Math.floor(monthIndex / 12);
    const monthNumber = (monthIndex % 12) + 1;
    const firstHalf = halfOffset === 0;
    return { year, monthNumber, firstHalf };
};

export const buildInvoiceOptionsByYearFromFirstBilling = (
    latestPeriod: DateAndMonth,
    firstBillingDateIso: string
): Record<number, DateAndMonth[]> => {
    const firstPeriod = dateToPeriod(new Date(firstBillingDateIso));
    const latestIdx = toLinearIndex(latestPeriod);
    const firstIdx = toLinearIndex(firstPeriod);
    const steps = Math.max(1, latestIdx - firstIdx + 1);
    const res: Record<number, DateAndMonth[]> = {};
    for (let i = 0; i < steps; i++) {
        const p = fromLinearIndex(latestIdx - i);
        const opt: DateAndMonth = { ...p, toShow: toShowLabel(p) };
        if (!res[p.year]) {
            res[p.year] = []
        }
        res[p.year].push(opt);
    }
    for (const y of Object.keys(res)) {
        res[Number(y)].sort(comparePeriodsDesc);
    }
    return res;
};